import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MuteFriendModal = ({ isOpen, onClose, onConfirm, friendName, isMuted }) => {
  const { t } = useTranslation('chat');
  // Default to "1 hour" if not muted, null if muted (for unmute)
  const [selectedDuration, setSelectedDuration] = useState(isMuted ? null : 1);

  const durations = [
    { label: t('muteFriendModal.durations.hour'), value: 1, unit: 'hour' },
    { label: t('muteFriendModal.durations.day'), value: 1, unit: 'day' },
    { label: t('muteFriendModal.durations.forever'), value: 'forever', unit: 'forever' },
  ];

  const handleConfirm = () => {
    if (isMuted) {
      // Unmute: set mutedUntil to null
      onConfirm(null);
    } else {
      // Mute: calculate mutedUntil based on selected duration
      const selectedDurationObj = durations.find(d => d.value === selectedDuration);
      if (selectedDurationObj && selectedDurationObj.unit === 'forever') {
        // Forever: set to a far future date (100 years from now)
        const foreverDate = new Date();
        foreverDate.setFullYear(foreverDate.getFullYear() + 100);
        onConfirm(foreverDate.toISOString());
      } else if (selectedDurationObj) {
        const mutedUntil = new Date();
        if (selectedDurationObj.unit === 'hour') {
          mutedUntil.setHours(mutedUntil.getHours() + selectedDurationObj.value);
        } else if (selectedDurationObj.unit === 'day') {
          mutedUntil.setDate(mutedUntil.getDate() + selectedDurationObj.value);
        }
        onConfirm(mutedUntil.toISOString());
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                    {isMuted ? t('muteFriendModal.titleUnmute') : t('muteFriendModal.titleMute')}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {isMuted ? (
                  <div className="space-y-4">
                    <p className="text-[var(--color-text-secondary)] dark:text-gray-300">
                      {t('muteFriendModal.unmuteConfirmPrefix')} <span className="font-semibold">{friendName}</span>{t('muteFriendModal.unmuteConfirmSuffix')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-[var(--color-text-secondary)] dark:text-gray-300">
                      {t('muteFriendModal.mutePromptPrefix')} <span className="font-semibold">{friendName}</span>{t('muteFriendModal.mutePromptSuffix')}
                    </p>
                    <div className="space-y-2">
                      {durations.map((duration) => {
                        // For "forever", use a special value to distinguish from null
                        const durationValue = duration.unit === 'forever' ? 'forever' : duration.value;
                        return (
                          <motion.button
                            key={duration.unit}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setSelectedDuration(duration.value)}
                            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                              selectedDuration === duration.value
                                ? 'border-[#3d8ef0]/50 bg-primary-50 dark:bg-[#2563eb]/10/20 text-[#3d8ef0] dark:text-primary-300'
                                : 'border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31]/60 text-[var(--color-text-secondary)] dark:text-gray-300 hover:border-white/10 dark:hover:border-dark-500'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{duration.label}</span>
                              {selectedDuration === duration.value && (
                                <div className="w-2 h-2 rounded-full bg-[#2563eb]" />
                              )}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 dark:border-white/10">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-[var(--color-bg-elevated)] dark:bg-[#161f31] hover:bg-[#24354f] dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-gray-300 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!isMuted && selectedDuration === null}
                  className={`px-4 py-2 rounded-xl transition-colors ${
                    !isMuted && selectedDuration === null
                      ? 'bg-[#24354f] dark:bg-[#161f31] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] cursor-not-allowed'
                      : 'bg-[#2563eb] hover:bg-[#3d8ef0] text-white'
                  }`}
                >
                  {isMuted ? t('muteFriendModal.confirmUnmute') : t('muteFriendModal.confirmMute')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MuteFriendModal;

